import AppConfigData from 'aws-sdk/clients/appconfigdata';
import CONSTANTS from '../utils/constants';

interface FeatureFlags {
  metricsDashboardEnabled: boolean;
  accountingCalendarEnabled: boolean;
  isPassthroughDrilldownEnabled: boolean;
  allowedUsers: string[];
  editLockingEnabled: boolean;
  testedPartyEnabled: boolean;
  standardizePLIEnabled: boolean;
  promotionEnabled: boolean;
  softCloseButtonEnabled: boolean;
  eventDrivenTpTaxReportingEnabled: boolean;
  segmentCdtEnabled: boolean;
  bulkPromotionEnabled: boolean;
  readOnlyModeEnabled: boolean;
  commonBalancePoolPhaseTwoEnabled: boolean;
  reportCurrencyOverlayEnabled: boolean;
  aiInsightsEnabled: boolean;
  tpTaxCliEnrichmentEnabled: boolean;
}

export const EDIT_LOCKING_FLAG = 'editLockingEnabled' as const;
export const AI_INSIGHTS_FLAG = 'aiInsightsEnabled' as const;

const DEFAULT_FLAGS: FeatureFlags = {
  metricsDashboardEnabled: false,
  accountingCalendarEnabled: false,
  isPassthroughDrilldownEnabled: false,
  allowedUsers: [],
  editLockingEnabled: false,
  testedPartyEnabled: false,
  standardizePLIEnabled: false,
  promotionEnabled: false,
  softCloseButtonEnabled: false,
  eventDrivenTpTaxReportingEnabled: false,
  segmentCdtEnabled: false,
  bulkPromotionEnabled: false,
  readOnlyModeEnabled: false,
  commonBalancePoolPhaseTwoEnabled: false,
  reportCurrencyOverlayEnabled: false,
  aiInsightsEnabled: false,
  tpTaxCliEnrichmentEnabled: false,
};

let cachedFlags: FeatureFlags | null = null;
let cachedToken: string | null = null;

// Sync accessor for AppPermissions.getPermissions() which runs during render.
// Every exit path of getFeatureFlags() must call syncReadOnlyCache() so error
// paths don't silently retain a stale default. Defaults to false.
let cachedReadOnlyMode = false;
const syncReadOnlyCache = (value: boolean): void => { cachedReadOnlyMode = value; };
export const isReadOnlyModeEnabled = (): boolean => cachedReadOnlyMode;

// Sync accessor for getRoutes(), which builds <Route>s during render (App gates render until
// getFeatureFlags() resolves, so this is populated by then). Mirrors isReadOnlyModeEnabled: every
// exit path of getFeatureFlags() sets it. Defaults to false so an absent/failed flag stays gated.
let cachedCommonBalancePoolPhaseTwo = false;
const syncCbpCache = (value: boolean): void => { cachedCommonBalancePoolPhaseTwo = value; };
export const isCommonBalancePoolPhaseTwoEnabled = (): boolean => cachedCommonBalancePoolPhaseTwo;

export async function getFeatureFlags(): Promise<FeatureFlags> {
  if (cachedFlags) {
    return cachedFlags;
  }

  const stage = CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE;
  if (!stage || stage.startsWith('{{') || stage === 'Dev') {
    syncReadOnlyCache(false);
    syncCbpCache(true);
    return {
      metricsDashboardEnabled: true,
      accountingCalendarEnabled: true,
      allowedUsers: [],
      editLockingEnabled: false,
      isPassthroughDrilldownEnabled: true,
      testedPartyEnabled: true,
      standardizePLIEnabled: true,
      promotionEnabled: true,
      softCloseButtonEnabled: true,
      eventDrivenTpTaxReportingEnabled: true,
      segmentCdtEnabled: true,
      bulkPromotionEnabled: true,
      readOnlyModeEnabled: false,
      commonBalancePoolPhaseTwoEnabled: true,
      reportCurrencyOverlayEnabled: true,
      aiInsightsEnabled: true,
      tpTaxCliEnrichmentEnabled: true,
    };
  }

  try {
    const client = new AppConfigData({
      region: CONSTANTS.ENVIRONMENT_VARIABLES.AWS_REGION,
    });

    const session = await client.startConfigurationSession({
      ApplicationIdentifier: 'ICTPFeatureFlagsApplication',
      EnvironmentIdentifier: 'ICTPFeatureFlagsEnvironment',
      ConfigurationProfileIdentifier: 'ICTPFeatureFlagsProfile',
    }).promise();

    cachedToken = session.InitialConfigurationToken || null;

    if (!cachedToken) {
      syncReadOnlyCache(false);
      syncCbpCache(false);
      return DEFAULT_FLAGS;
    }

    const config = await client.getLatestConfiguration({
      ConfigurationToken: cachedToken,
    }).promise();

    if (config.Configuration) {
      const parsed = JSON.parse(config.Configuration.toString());
      cachedFlags = {
        metricsDashboardEnabled: parsed?.metricsDashboardEnabled?.enabled ?? false,
          accountingCalendarEnabled: parsed?.accountingCalendarEnabled?.enabled ?? false,
          isPassthroughDrilldownEnabled: parsed?.isPassthroughDrilldownEnabled?.enabled ?? false,
        allowedUsers: parsed?.metricsDashboardEnabled?.allowedUsers ?? [],
        editLockingEnabled: parsed?.editLockingEnabled?.enabled ?? false,
        testedPartyEnabled: parsed?.testedPartyEnabled?.enabled ?? false,
        standardizePLIEnabled: parsed?.standardizePLIEnabled?.enabled ?? false,
        promotionEnabled: parsed?.promotionEnabled?.enabled ?? false,
        softCloseButtonEnabled: parsed?.softCloseButtonEnabled?.enabled ?? false,
        eventDrivenTpTaxReportingEnabled: parsed?.eventDrivenTpTaxReportingEnabled?.enabled ?? false,
        segmentCdtEnabled: parsed?.segmentCdtEnabled?.enabled ?? false,
        bulkPromotionEnabled: parsed?.bulkPromotionEnabled?.enabled ?? false,
        readOnlyModeEnabled: parsed?.readOnlyModeEnabled?.enabled ?? false,
        commonBalancePoolPhaseTwoEnabled: parsed?.commonBalancePoolPhaseTwoEnabled?.enabled ?? false,
        reportCurrencyOverlayEnabled: parsed?.reportCurrencyOverlayEnabled?.enabled ?? false,
        aiInsightsEnabled: parsed?.aiInsightsEnabled?.enabled ?? false,
        tpTaxCliEnrichmentEnabled: parsed?.tpTaxCliEnrichmentEnabled?.enabled ?? false,
      };
      syncReadOnlyCache(cachedFlags.readOnlyModeEnabled);
      syncCbpCache(cachedFlags.commonBalancePoolPhaseTwoEnabled);
      return cachedFlags;
    }
  } catch (error) {
    console.error('Failed to fetch feature flags from AppConfig:', error);
  }

  // Empty config or fetch threw. Default read-only mode off and CBP gated off.
  syncReadOnlyCache(false);
  syncCbpCache(false);
  return DEFAULT_FLAGS;
}

export function isFeatureEnabledForUser(flags: FeatureFlags, featureName: 'metricsDashboardEnabled' | 'editLockingEnabled' | 'testedPartyEnabled' | 'softCloseButtonEnabled' | 'aiInsightsEnabled', userAlias: string): boolean {
  if (!flags[featureName]) {
    return false;
  }
  if (featureName === 'metricsDashboardEnabled') {
    if (flags.allowedUsers.length === 0) {
      return true;
    }
    return flags.allowedUsers.includes(userAlias);
  }
  return true;
}
